import { useApi } from '@/config/apiFetcher'
import { useQueryClient, type QueryClient } from '@tanstack/vue-query'
import { useUserStore } from '@/store/userStore'
import { storeToRefs } from 'pinia'
import { ref, getCurrentInstance } from 'vue'
import { saveUserSettingsLocal, type UserSettingsLocal } from '@/utils/indexedDb'
import log from '@/utils/useLogger'
import { UserSettings } from '@/store/userSettings'
import { throttle } from 'lodash'

export default () => {
  const api = useApi()
  const baseUrl = import.meta.env.VITE_USER_SETTINGS
  const { userId } = storeToRefs(useUserStore())
  const userSettingsStore = UserSettings()
  const { settings, version } = storeToRefs(userSettingsStore)
  const { loadLocalSettings, updateSettings, setVersion } = userSettingsStore

  // Safely get query client - only works in setup context
  // This allows the composable to be used outside setup for read-only operations
  let queryClient: QueryClient | null = null
  if (getCurrentInstance()) {
    try {
      queryClient = useQueryClient()
    } catch {
      // Not in setup context or VueQueryPlugin not installed
    }
  }

  const isSyncing = ref(false)
  const isInitialized = ref(false)

  /**
   * Initialize user settings: load from IndexedDB cache (fast, no network).
   * Call this on app mount for instant settings availability.
   */
  const initUserSettings = async () => {
    if (!userId.value || isInitialized.value) return

    try {
      // Load from local cache immediately (fast, no network)
      await loadLocalSettings(userId.value)
      isInitialized.value = true
      log.info('User settings loaded from cache')
    } catch (err) {
      log.error('Failed to load settings from cache', err)
    }
  }

  /**
   * Fetch latest settings from cloud. Only call this:
   * - On login (to sync cross-device changes)
   * - When explicitly needed (e.g., pull-to-refresh)
   * Returns the cloud data or null if failed.
   */
  const fetchFromCloud = async () => {
    if (!userId.value) return null

    try {
      log.info('Fetching settings from cloud')
      const res = await api.get(`${baseUrl}/user-settings/${userId.value}`)
      const cloudSettings = res.data as UserSettingsLocal
      const deserializedSettings = deserializeFromDynamo(cloudSettings.settings)

      // Save to IndexedDB and update store
      await saveUserSettingsLocal(
        userId.value,
        deserializedSettings,
        cloudSettings.version,
        cloudSettings.updated_at,
      )
      await loadLocalSettings(userId.value)
      log.info('Settings synced from cloud')
      return { ...cloudSettings, settings: deserializedSettings }
    } catch (err: any) {
      // 404 means user settings don't exist yet - create them on cloud
      if (err.response?.status === 404) {
        log.info('No cloud settings found, creating with local defaults')
        return await createCloudSettings()
      }
      log.error('Failed to fetch settings from cloud', err)
      return null
    }
  }

  // Create new settings on cloud (for first-time users)
  const createCloudSettings = async () => {
    if (!userId.value) return null
    try {
      const res = await api.put(`${baseUrl}/user-settings/${userId.value}`, {
        settings: settings.value,
      })
      if (res.data?.version) {
        setVersion(res.data.version)
      }
      return res.data
    } catch (err: any) {
      log.error('Failed to create user settings on cloud', err)
      return null
    }
  }

  // Internal function to sync current store state to cloud
  const syncToCloud = async () => {
    log.info('Syncing settings to cloud')
    if (!userId.value || isSyncing.value) return

    isSyncing.value = true
    try {
      const res = await api.put(`${baseUrl}/user-settings/${userId.value}`, {
        settings: settings.value,
        version: version.value,
      })
      // Update local version from backend response
      if (res.data?.version) {
        setVersion(res.data.version)
      }
      queryClient?.invalidateQueries({ queryKey: ['user-settings', userId.value] })
      return res.data
    } catch (err: any) {
      log.error('Failed to sync settings to cloud', err)
      if (err.response?.status === 409) {
        return await mergeOnConflict()
      }
      throw err
    } finally {
      isSyncing.value = false
    }
  }

  // Throttled cloud sync - uses latest store value when executed
  // 10 seconds is reasonable since local save is immediate (IndexedDB)
  // and this just syncs to cloud for backup/cross-device access
  const throttledSyncToCloud = throttle(syncToCloud, 10000, {
    leading: false,
    trailing: true,
  })

  /**
   * Save settings: optimistic local update + throttled cloud sync
   * @param newSettings - partial settings to merge
   */
  const saveSettings = async (newSettings: Record<string, any>) => {
    if (!userId.value) {
      log.error('Cannot save settings: no user ID')
      return false
    }
    const success = await updateSettings(userId.value, newSettings)
    log.info('Success:', success)
    if (success) {
      // Queue throttled cloud sync
      log.info('Queueing throttled cloud sync')
      throttledSyncToCloud()
    }

    return success
  }

  // Force immediate cloud sync (e.g., before logout)
  const forceSyncToCloud = async () => {
    throttledSyncToCloud.cancel()
    return await syncToCloud()
  }

  /**
   * Cancel any pending throttled sync.
   * Call this in onUnmounted to prevent trailing syncs after component teardown.
   */
  const cancelPendingSync = () => {
    throttledSyncToCloud.cancel()
  }

  /**
   * Handle version conflict (409):
   * If our version < cloud version, cloud is more up-to-date.
   * Fetch cloud settings, save locally, then retry sync with merged changes.
   */
  const mergeOnConflict = async () => {
    try {
      log.info('Version conflict detected, fetching latest from cloud')
      const localSettingsBeforeSync = JSON.parse(JSON.stringify(settings.value))

      const latestRes = await api.get(`${baseUrl}/user-settings/${userId.value}`)
      const cloudSettings = deserializeFromDynamo(latestRes.data.settings)
      const cloudVersion = latestRes.data.version
      const cloudUpdatedAt = latestRes.data.updated_at

      // Cloud version is newer - prioritize it
      // Save cloud settings to local first
      await saveUserSettingsLocal(userId.value!, cloudSettings, cloudVersion, cloudUpdatedAt)

      // Reload local settings to update store with cloud data
      await loadLocalSettings(userId.value!)
      log.info(`Synced from cloud (version ${cloudVersion})`)

      // Now merge any local changes that weren't in cloud
      // Use the captured local settings (before overwrite) so local changes are preserved
      const mergedSettings = deepMerge(cloudSettings, localSettingsBeforeSync)

      // Only retry if there are actual differences after merge
      if (JSON.stringify(mergedSettings) !== JSON.stringify(cloudSettings)) {
        log.info('Retrying sync with merged settings')
        const retrySave = await api.put(`${baseUrl}/user-settings/${userId.value}`, {
          settings: mergedSettings,
          version: cloudVersion,
        })
        if (retrySave.data?.version) {
          setVersion(retrySave.data.version)
        }
        // Update local with merged settings so UI reflects the merge
        await saveUserSettingsLocal(
          userId.value!,
          mergedSettings,
          retrySave.data.version,
          retrySave.data.updated_at,
        )
        await loadLocalSettings(userId.value!)
        queryClient?.invalidateQueries({ queryKey: ['user-settings', userId.value] })
        return retrySave.data
      }

      queryClient?.invalidateQueries({ queryKey: ['user-settings', userId.value] })
      return latestRes.data
    } catch (err: any) {
      log.error('Failed to resolve version conflict', err)
      throw err
    }
  }

  const deepMerge = (base: any, updates: any): any => {
    const result = { ...base }
    for (const key in updates) {
      const baseVal = base[key]
      const updateVal = updates[key]

      // Check if both are plain objects (not null, not arrays)
      const isBaseObject =
        baseVal !== null && typeof baseVal === 'object' && !Array.isArray(baseVal)
      const isUpdateObject =
        updateVal !== null && typeof updateVal === 'object' && !Array.isArray(updateVal)

      if (isBaseObject && isUpdateObject) {
        // Recursively merge plain objects
        result[key] = deepMerge(baseVal, updateVal)
      } else {
        // For arrays, nulls, primitives, or mismatched types: replace with update value
        // Clone arrays to avoid reference issues
        result[key] = Array.isArray(updateVal) ? [...updateVal] : updateVal
      }
    }
    return result
  }

  /**
   * Convert string numbers back to actual numbers when loading from DynamoDB
   * DynamoDB returns Decimals as strings (e.g., "0.5" instead of 0.5)
   */
  const deserializeFromDynamo = (obj: any): any => {
    if (obj === null || obj === undefined) return obj
    if (Array.isArray(obj)) return obj.map(deserializeFromDynamo)
    if (typeof obj === 'object') {
      const result: Record<string, any> = {}
      for (const key in obj) {
        result[key] = deserializeFromDynamo(obj[key])
      }
      return result
    }
    // Convert numeric strings back to numbers
    if (typeof obj === 'string' && obj.trim() !== '' && /^-?\d+(\.\d+)?$/.test(obj)) {
      return Number(obj)
    }
    return obj
  }

  return {
    initUserSettings,
    fetchFromCloud,
    saveSettings,
    forceSyncToCloud,
    cancelPendingSync,
    // State
    isSyncing,
    isInitialized,
    settings,
    version,
  }
}
